import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { AuthService } from '../../../services/auth-service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatTooltipModule,
    RouterModule,
    CommonModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
})
export class SidenavComponent implements OnInit, OnDestroy {
  isCollapsed = true;
  isMobile = false;
  isSettingsMenuOpen = false;
  isAnalyticsMenuOpen = false;
  isAppAdmin = false;

  private readonly _destroying$ = new Subject<void>();
  private settingsMenuTimeout: ReturnType<typeof setTimeout> | null = null;
  private analyticsMenuTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly platformId = inject(PLATFORM_ID);

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {
    this.checkScreenSize();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!(event.target as HTMLElement).closest('.dropdown-container')) {
      this.isSettingsMenuOpen = false;
      this.isAnalyticsMenuOpen = false;
    }
  }

  ngOnInit(): void {
    this.isCollapsed = true;
    this.initializeProfile();
  }

  private initializeProfile(): void {
    this.authService.profileSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => this.updateRoleFlags());
  }

  private updateRoleFlags(): void {
    const roleCodes = (this.authService.getProfileDetail()?.roleCode ?? '')
      .split(',')
      .map((code) => code.trim().toUpperCase());
    this.isAppAdmin = roleCodes.includes('AAS');
  }

  private checkScreenSize(): void {
    if (isPlatformBrowser(this.platformId)) {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;
      if (this.isMobile && !wasMobile) {
        this.isCollapsed = true;
      }
    }
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
    if (this.isCollapsed) {
      this.isSettingsMenuOpen = false;
      this.isAnalyticsMenuOpen = false;
    }
  }

  showSettingsMenu(): void {
    if (!this.isMobile) {
      this.clearSettingsTimeout();
      this.isSettingsMenuOpen = true;
    }
  }

  hideSettingsMenu(): void {
    if (!this.isMobile) {
      this.settingsMenuTimeout = setTimeout(
        () => (this.isSettingsMenuOpen = false),
        150,
      );
    }
  }

  toggleSettingsMenu(): void {
    this.isSettingsMenuOpen = !this.isSettingsMenuOpen;
  }

  showAnalyticsMenu(): void {
    if (!this.isMobile) {
      this.clearAnalyticsTimeout();
      this.isAnalyticsMenuOpen = true;
    }
  }

  hideAnalyticsMenu(): void {
    if (!this.isMobile) {
      this.analyticsMenuTimeout = setTimeout(
        () => (this.isAnalyticsMenuOpen = false),
        150,
      );
    }
  }

  toggleAnalyticsMenu(): void {
    this.isAnalyticsMenuOpen = !this.isAnalyticsMenuOpen;
  }

  navigateAndClose(route: string): void {
    this.router.navigate([route]);
    this.isSettingsMenuOpen = false;
    this.isAnalyticsMenuOpen = false;
    if (this.isMobile && !this.isCollapsed) {
      this.isCollapsed = true;
    }
  }

  private clearSettingsTimeout(): void {
    if (this.settingsMenuTimeout) {
      clearTimeout(this.settingsMenuTimeout);
      this.settingsMenuTimeout = null;
    }
  }

  private clearAnalyticsTimeout(): void {
    if (this.analyticsMenuTimeout) {
      clearTimeout(this.analyticsMenuTimeout);
      this.analyticsMenuTimeout = null;
    }
  }

  ngOnDestroy(): void {
    this.clearSettingsTimeout();
    this.clearAnalyticsTimeout();
    this._destroying$.next();
    this._destroying$.complete();
  }
}
